import { computed, type Ref } from "vue";
import { useHead } from "#imports";
import type { locationObject } from "~/types";

/**
 * Injects a JSON-LD CollectionPage schema (https://schema.org/CollectionPage)
 * into <head> for city/location job listing pages.
 */
export const useLocationPageSchema = (
	locationTag: Ref<locationObject> | null,
	pageTitle: Ref<string>,
	pageDescription: Ref<string>,
	canonicalUrl: Ref<string>,
	locale: Ref<string>
) => {
	const jsonLdSchema = computed(() => {
		if (!locationTag || !locationTag.value) return null;

		const loc = locationTag.value;
		const cityName = loc.name[locale.value as keyof typeof loc.name] || loc.name["en"] || loc.slug;

		const schema: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "CollectionPage",
			name: pageTitle.value,
			description: pageDescription.value,
			url: canonicalUrl.value,
			inLanguage: locale.value,
			about: {
				"@type": "Place",
				name: cityName,
				identifier: String(loc.id),
			},
		};

		return schema;
	});

	if (jsonLdSchema.value) {
		useHead(() => ({
			script: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(jsonLdSchema.value).replace(/</g, "\\u003c"),
					key: "article-schema",
				},
			],
		}));
	}
};
